import { gsap, ScrollTrigger, createScrollTrigger, killTriggersForElement } from "../core/gsap";
import { responsiveMediaAbove } from "../../settings/responsive";
import { SELECTOR_SECTION } from "../../sections/image-and-text-columns";

const SELECTOR_ASIDE_MEDIA_WRAPPER = '[data-aside-media-wrapper]';
const SELECTOR_IMAGE_TEXT_BODY = '[data-image-and-text-columns-body]';
const SELECTOR_ANIMATION = {
  BG: '[data-bg]',
  ASIDE_MEDIA: '[data-aside-media]',
  ACCORDIONS: '[data-accordions]'
}

// Store active ScrollTrigger instances per section
const activeTriggers = new WeakMap<HTMLElement, ScrollTrigger>();

/**
 * Creates a parallax background scroll animation with optimized settings.
 */
function createBackgroundParallax(bgEl: HTMLElement, triggerEl: HTMLElement) {
  createScrollTrigger(bgEl, {
    y: -200,
    ease: "none",
    scrollTrigger: {
      trigger: triggerEl,
      start: "top top",
      end: "bottom top",
      scrub: true,
      markers: false,
      invalidateOnRefresh: true
    }
  });
}

/**
 * Calculate the end position for aside media based on accordion states
 */
function calculateAsideMediaEndPosition(
  asideMediaEl: HTMLElement,
  asideMediaWrapperEl: HTMLElement,
  accordionsEl: HTMLElement | null,
  offsetTopStart: number,
  scrollEndEl: HTMLElement | null
): string {
  const isDesktop = responsiveMediaAbove("medium");
  
  if (isDesktop) {
    return `bottom bottom-=100`;
  }

  // Mobile: Use scrollEndEl (accordions container) as reference
  if (!scrollEndEl) {
    return `bottom top`;
  }

  // Convert 1.63rem to pixels (assuming 16px base font size)
  const remToPx = (rem: number) => rem * 16;
  const desiredGap = remToPx(1.63); // 1.63rem gap
  
  // Mobile end position (for both open and closed accordions)
  // End when aside image bottom is 1.63rem above accordion top
  // When pinned at top+offsetTopStart and scrollEndEl.top reaches viewport top,
  // the image bottom should be at: offsetTopStart + imageHeight
  // We want this to align with: scrollEndEl.top - desiredGap
  // So: end is when scrollEndEl.top = offsetTopStart + imageHeight + desiredGap
  const offset = offsetTopStart + asideMediaEl.offsetHeight + desiredGap;
  return `top top+=${offset}`;
}

/**
 * Initializes sticky aside media with ScrollTrigger pinning and scroll effects.
 * Uses performance optimizations and proper cleanup.
 */
function initStickyAside(
  asideMediaEl: HTMLElement,
  asideMediaWrapperEl: HTMLElement,
  imageTextBodyEl: HTMLElement | null,
  carouselContainer: HTMLElement | null,
  accordionsEl: HTMLElement | null
) {
  const isDesktop = responsiveMediaAbove("medium");
  const offsetTopStart = isDesktop ? 100 : 52;
  
  // Mobile: use accordions as endTrigger if no carousel exists
  const scrollEndEl = isDesktop 
    ? imageTextBodyEl 
    : (carouselContainer || accordionsEl);
    
  const scrollEndOffset = calculateAsideMediaEndPosition(
    asideMediaEl,
    asideMediaWrapperEl,
    accordionsEl,
    offsetTopStart,
    scrollEndEl
  );

  const trigger = createScrollTrigger(asideMediaEl, {
    y: 0,
    ease: "none",
    scrollTrigger: {
      trigger: asideMediaWrapperEl,
      start: `top top+=${offsetTopStart}`,
      endTrigger: scrollEndEl,
      end: scrollEndOffset,
      pin: true,
      pinSpacing: false,
      invalidateOnRefresh: true,
      scrub: true,
      // Removed anticipatePin - it's causing premature visibility
    }
  });

  // Store the trigger for later updates
  if (trigger && trigger.scrollTrigger) {
    activeTriggers.set(asideMediaEl, trigger.scrollTrigger);
  }
}

/**
 * Update aside media animation based on accordion state changes
 */
export function updateAsideMediaAnimation(sectionEl: HTMLElement) {
  const asideMediaEl = sectionEl.querySelector<HTMLElement>(SELECTOR_ANIMATION.ASIDE_MEDIA);
  const asideMediaWrapperEl = sectionEl.querySelector<HTMLElement>(SELECTOR_ASIDE_MEDIA_WRAPPER);
  const accordionsEl = sectionEl.querySelector<HTMLElement>(SELECTOR_ANIMATION.ACCORDIONS);
  const imageTextBodyEl = sectionEl.querySelector<HTMLElement>(SELECTOR_IMAGE_TEXT_BODY);
  const carouselContainer = sectionEl.querySelector<HTMLElement>('[data-swiper-container]');
  
  if (!asideMediaEl || !asideMediaWrapperEl) return;
  
  const trigger = activeTriggers.get(asideMediaEl);
  if (!trigger) return;
  
  const isDesktop = responsiveMediaAbove("medium");
  const offsetTopStart = isDesktop ? 100 : 52;
  
  // Get the scroll end element
  const scrollEndEl = isDesktop 
    ? imageTextBodyEl 
    : (carouselContainer || accordionsEl);
  
  // Recalculate end position based on current accordion states
  const newEndOffset = calculateAsideMediaEndPosition(
    asideMediaEl,
    asideMediaWrapperEl,
    accordionsEl,
    offsetTopStart,
    scrollEndEl
  );
  
  // Update the ScrollTrigger end position
  trigger.vars.end = newEndOffset;
  trigger.refresh();
}

export function initImageAndTextColumns() {
  document.querySelectorAll<HTMLElement>(SELECTOR_SECTION).forEach((el) => {
    initSection(el);
  });
}

function initSection(el: HTMLElement) {
  // Skip if already initialized
  if (el.dataset.gsapInit === "true") return;

  // Select required elements
  const imageTextBody = el.querySelector<HTMLElement>(SELECTOR_IMAGE_TEXT_BODY);
  const bgEl = el.querySelector<HTMLElement>(SELECTOR_ANIMATION.BG);
  const asideMediaEl = el.querySelector<HTMLElement>(SELECTOR_ANIMATION.ASIDE_MEDIA);
  const asideMediaWrapperEl = el.querySelector<HTMLElement>(SELECTOR_ASIDE_MEDIA_WRAPPER);
  const carouselContainer = el.querySelector<HTMLElement>('[data-swiper-container]');
  const accordionsEl = el.querySelector<HTMLElement>(SELECTOR_ANIMATION.ACCORDIONS);

  // Guard against missing critical elements
  if (!bgEl || !asideMediaEl || !asideMediaWrapperEl) return;

  // Kill old triggers efficiently
  killTriggersForElement(el);

  // Initialize animations
  createBackgroundParallax(bgEl, el);
  initStickyAside(asideMediaEl, asideMediaWrapperEl, imageTextBody, carouselContainer, accordionsEl);

  // Mark as initialized
  el.dataset.gsapInit = "true";
}
